import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  createColumnHelper,
  type SortingState,
} from '@tanstack/react-table';
import dayjs from 'dayjs';
import type { BpOrder, BpOrderLine } from '@/api/business-partner/business-partner.types';
import { useGetOrderLinesByDocEntry } from '@/api/business-partner/business-partner.hooks';
import { DsKv } from '@/modules/internal-v2/ds';
import { DsTable } from '../../shared/ds-table.component';
import { DsSearchInput } from '@/modules/internal-v2/ds';

const fmtEUR = (v: number) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v);

const col = createColumnHelper<BpOrder>();

const columns = [
  col.accessor('docNum', {
    header: 'Nr.',
    size: 70,
    meta: { numeric: true },
    cell: (info) => (
      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 500 }}>{info.getValue()}</span>
    ),
  }),
  col.accessor('numAtCard', {
    header: 'Referenz',
    size: 130,
    cell: (info) => info.getValue() || <span style={{ color: 'var(--ink-4)' }}>—</span>,
  }),
  col.accessor('items', {
    header: 'Artikel',
    meta: { flex: true },
    cell: (info) => {
      const items = info.getValue();
      if (!items) return <span style={{ color: 'var(--ink-4)' }}>—</span>;
      const truncated = items.length > 80 ? items.slice(0, 80) + '…' : items;
      return (
        <span style={{ color: 'var(--ink-2)', fontSize: 'var(--fs-xs)' }} title={items}>
          {truncated}
        </span>
      );
    },
    enableSorting: false,
  }),
  col.accessor('lineCount', {
    header: 'Pos.',
    size: 50,
    meta: { numeric: true },
    cell: (info) => (
      <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink-2)' }}>{info.getValue()}</span>
    ),
  }),
  col.accessor('docDate', {
    header: 'Datum',
    size: 85,
    cell: (info) => (
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}>
        {dayjs(info.getValue()).format('DD.MM.YY')}
      </span>
    ),
    sortingFn: (a, b) => {
      const da = new Date(a.original.docDate).getTime();
      const db = new Date(b.original.docDate).getTime();
      return da - db;
    },
  }),
  col.accessor('docTotal', {
    header: 'Total',
    size: 110,
    meta: { numeric: true },
    cell: (info) => (
      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 500 }}>{fmtEUR(info.getValue())}</span>
    ),
  }),
  col.accessor('documentStatus', {
    header: 'Status',
    size: 90,
    cell: (info) => {
      const closed = info.getValue() === 'bost_Close';
      return (
        <span className={`bp-status ${closed ? 'closed' : 'open'}`}>
          {closed ? 'Geschl.' : 'Offen'}
        </span>
      );
    },
  }),
];

interface BpOrdersTabProps {
  businessPartnerId: number;
  orders: BpOrder[];
  total: number;
  isPending: boolean;
  page: number;
  setPage: (p: number) => void;
}

export function BpOrdersTab({ businessPartnerId, orders, total, isPending, page, setPage }: BpOrdersTabProps) {
  const navigate = useNavigate();
  const { docEntry } = useParams<{ docEntry: string }>();
  const [search, setSearch] = useState('');
  const [sorting, setSorting] = useState<SortingState>([{ id: 'docDate', desc: true }]);

  const ordersBase = `/i/business-partners/${businessPartnerId}/auftraege`;
  const selected = docEntry
    ? (orders.find((o) => String(o.docEntry) === docEntry) ?? null)
    : null;

  const filtered = useMemo(() => {
    if (!search) return orders;
    const q = search.toLowerCase();
    return orders.filter(
      (o) =>
        String(o.docNum).includes(q) ||
        (o.numAtCard?.toLowerCase().includes(q)) ||
        (o.items?.toLowerCase().includes(q)) ||
        String(o.docTotal).includes(q),
    );
  }, [orders, search]);

  const table = useReactTable({
    data: filtered,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => String(row.docEntry),
    enableSortingRemoval: false,
  });

  const pageSize = 50;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="bp-tab-host">
      <div className="bp-orders-toolbar">
        <DsSearchInput
          className="bp-orders-search"
          value={search}
          onSearch={setSearch}
          debounce={0}
          placeholder="Nr., Referenz, Artikel…"
        />
        <div className="bp-orders-pager">
          <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}>‹</button>
          <span>Seite <b style={{ color: 'var(--ink)' }}>{page + 1}</b> / {totalPages}</span>
          <button onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1}>›</button>
        </div>
      </div>
      <DsTable
        table={table}
        fixedLayout
        selectedRowId={docEntry ?? undefined}
        onRowClick={(o) => navigate(`${ordersBase}/${o.docEntry}`)}
        emptyMessage={isPending ? 'Laden…' : 'Keine Aufträge gefunden.'}
      />

      {selected && (
        <OrderOverlay
          order={selected}
          businessPartnerId={businessPartnerId}
          onClose={() => navigate(ordersBase)}
        />
      )}
    </div>
  );
}

/* ---- Order line columns ---- */

const lineCol = createColumnHelper<BpOrderLine>();

const lineColumns = [
  lineCol.accessor('lineNum', {
    header: '#',
    size: 40,
    meta: { numeric: true },
    cell: (info) => (
      <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink-3)' }}>{info.getValue()}</span>
    ),
  }),
  lineCol.accessor('itemCode', {
    header: 'Artikelnr.',
    size: 110,
    cell: (info) => (
      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 500, fontSize: 'var(--fs-xs)' }}>
        {info.getValue()}
      </span>
    ),
  }),
  lineCol.accessor('itemDescription', {
    header: 'Bezeichnung',
    meta: { flex: true },
    cell: (info) => (
      <span style={{ color: 'var(--ink)' }}>{info.getValue()}</span>
    ),
  }),
  lineCol.accessor('quantity', {
    header: 'Menge',
    size: 65,
    meta: { numeric: true },
    cell: (info) => (
      <span style={{ fontFamily: 'var(--font-mono)' }}>{info.getValue()}</span>
    ),
  }),
  lineCol.accessor('unitPrice', {
    header: 'Stückpr.',
    size: 95,
    meta: { numeric: true },
    cell: (info) => {
      const v = info.getValue();
      return (
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}>
          {v != null ? fmtEUR(v) : '—'}
        </span>
      );
    },
  }),
  lineCol.accessor('lineTotal', {
    header: 'Gesamt',
    size: 100,
    meta: { numeric: true },
    cell: (info) => (
      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 500 }}>{fmtEUR(info.getValue())}</span>
    ),
  }),
  lineCol.accessor('lineStatus', {
    header: 'Status',
    size: 75,
    cell: (info) => {
      const v = info.getValue();
      const closed = v === 'bost_Close';
      return (
        <span className={`bp-status ${closed ? 'closed' : 'open'}`}>
          {closed ? 'Geschl.' : 'Offen'}
        </span>
      );
    },
  }),
];

/* ---- Order overlay ---- */

function OrderOverlay({
  order,
  businessPartnerId,
  onClose,
}: {
  order: BpOrder;
  businessPartnerId: number;
  onClose: () => void;
}) {
  const closed = order.documentStatus === 'bost_Close';

  const { data: lines = [], isPending } = useGetOrderLinesByDocEntry(
    businessPartnerId,
    order.docEntry,
  );

  const linesTable = useReactTable({
    data: lines,
    columns: lineColumns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => String(row.lineNum),
  });

  return (
    <div className="bp-overlay">
      <div className="bp-overlay-bar">
        <button className="bp-cmd-back" onClick={onClose}>← Zurück</button>
        <span className="bp-overlay-title">Auftrag #{order.docNum}</span>
        <span className={`bp-status ${closed ? 'closed' : 'open'}`}>
          {closed ? 'Geschlossen' : 'Offen'}
        </span>
      </div>
      <div className="bp-overlay-section" style={{ padding: '12px 14px 0' }}>
        <DsKv
          keyWidth={120}
          entries={[
            {
              label: 'Referenz',
              value: order.numAtCard ? (
                <span style={{ fontFamily: 'var(--font-mono)' }}>{order.numAtCard}</span>
              ) : null,
              missing: !order.numAtCard,
            },
            {
              label: 'Datum',
              value: (
                <span style={{ fontFamily: 'var(--font-mono)' }}>
                  {dayjs(order.docDate).format('DD.MM.YYYY')}
                </span>
              ),
            },
            ...(order.docDueDate
              ? [{
                  label: 'Fällig',
                  value: (
                    <span style={{ fontFamily: 'var(--font-mono)' }}>
                      {dayjs(order.docDueDate).format('DD.MM.YYYY')}
                    </span>
                  ),
                }]
              : []),
            {
              label: 'Total',
              value: (
                <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
                  {fmtEUR(order.docTotal)}
                </span>
              ),
            },
          ]}
        />
      </div>
      <div className="bp-overlay-section" style={{ padding: '10px 14px 4px' }}>
        <div className="bp-overlay-section-label">
          Positionen <span className="bp-ov-count">{lines.length}</span>
        </div>
      </div>
      <DsTable
        table={linesTable}
        fixedLayout
        emptyMessage={isPending ? 'Laden…' : 'Keine Positionen.'}
      />
    </div>
  );
}
